// Engineering Review Queue Page - Enterprise-grade review queue
import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Skeleton,
  Alert,
  Tooltip,
  IconButton,
  Pagination,
  Stack,
  alpha,
  useTheme,
  Popover,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  AccessTime as TimeIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  UnfoldMore as SortIcon,
  OpenInNew as OpenIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { differenceInHours, format } from 'date-fns';
import {
  useEngineeringQueue,
  useEngineeringDashboardAll,
} from '../../hooks/useEngineeringReviews';
import type {
  EngineeringReview,
  EngineeringReviewStatus,
} from '../../services/engineeringReviewService';

type SortField = 'priority' | 'supplierScore' | 'confidence' | 'sla' | 'createdAt';

const EngineeringReviewQueuePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string[]>(['PENDING', 'IN_PROGRESS']);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [slaFilter, setSlaFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortField>('priority');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const { data: queueData, isLoading, error, refetch } = useEngineeringQueue({
    status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
    priority: priorityFilter.length > 0 ? priorityFilter.join(',') : undefined,
    slaBreached: slaFilter === 'breached' ? true : slaFilter === 'healthy' ? false : undefined,
    page,
    limit: 20,
    sortBy,
    sortOrder,
  });

  const { data: dashboard } = useEngineeringDashboardAll();

  // ── Helpers ──────────────────────────────────────────────

  const getSLAInfo = (review: EngineeringReview) => {
    if (review.status === 'COMPLETED' || review.status === 'CANCELLED') {
      return { label: '-', severity: 'default' as const };
    }
    if (review.slaBreached) {
      return { label: 'Overdue', severity: 'error' as const };
    }
    if (!review.dueAt) {
      return { label: '-', severity: 'default' as const };
    }
    const dueDate = new Date(review.dueAt);
    if (isNaN(dueDate.getTime())) {
      return { label: '-', severity: 'default' as const };
    }
    const hoursRemaining = differenceInHours(dueDate, new Date());
    const daysRemaining = Math.ceil(hoursRemaining / 24);
    if (daysRemaining <= 0) return { label: 'Today', severity: 'error' as const };
    if (daysRemaining === 1) return { label: '1 day', severity: 'warning' as const };
    if (daysRemaining <= 3) return { label: `${daysRemaining} days`, severity: 'warning' as const };
    return { label: `${daysRemaining} days`, severity: 'default' as const };
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 85) return theme.palette.success.main;
    if (score >= 70) return theme.palette.primary.main;
    if (score >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getStatusColor = (status: EngineeringReviewStatus) => {
    switch (status) {
      case 'PENDING': return 'default';
      case 'IN_PROGRESS': return 'primary';
      case 'COMPLETED': return 'success';
      case 'ESCALATED': return 'warning';
      case 'CANCELLED': return 'default';
      default: return 'default';
    }
  };

  const getPriorityBorderColor = (priority: string) => {
    switch (priority) {
      case 'EMERGENCY':
      case 'CRITICAL': return theme.palette.error.main;
      case 'HIGH': return theme.palette.warning.main;
      case 'STANDARD': return theme.palette.warning.light;
      default: return theme.palette.divider;
    }
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleRowClick = (review: EngineeringReview) => {
    // Use engineeringReviewId if available, otherwise fall back to alternativePartId for cross-ref based lookup
    const id = review.engineeringReviewId || review._id;
    // Pass crossReferenceId so the detail page resolves the exact cross-ref
    // (multiple cross-refs can share the same alternativePartId)
    const xrefId = (review as any).crossReferenceId;
    const search = xrefId ? `?crossReferenceId=${xrefId}` : '';
    navigate(`/engineering/reviews/${id}${search}`);
  };

  // ── KPI Stats ──────────────────────────────────────────────

  const stats = [
    { label: 'Pending', value: dashboard?.pending ?? 0, filter: () => setStatusFilter(['PENDING']), active: statusFilter.length === 1 && statusFilter[0] === 'PENDING' },
    { label: 'In Progress', value: dashboard?.inProgress ?? 0, filter: () => setStatusFilter(['IN_PROGRESS']), active: statusFilter.length === 1 && statusFilter[0] === 'IN_PROGRESS' },
    { label: 'SLA Breached', value: dashboard?.slaBreached ?? 0, filter: () => setSlaFilter(slaFilter === 'breached' ? 'all' : 'breached'), active: slaFilter === 'breached' },
    { label: 'At Risk', value: dashboard?.atRisk ?? 0 },
    { label: 'Completed Today', value: dashboard?.completedToday ?? 0 },
    { label: 'Approval Rate', value: dashboard?.approvalRate ?? 0, suffix: '%' },
  ];

  // ── Filter Popover State ─────────────────────────────────

  const [filterAnchor, setFilterAnchor] = useState<{ el: HTMLElement; type: 'status' | 'priority' | 'sla' } | null>(null);

  const statusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'ESCALATED', label: 'Escalated' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  const priorityOptions = [
    { value: 'EMERGENCY', label: 'Emergency' },
    { value: 'CRITICAL', label: 'Critical' },
    { value: 'HIGH', label: 'High' },
    { value: 'STANDARD', label: 'Standard' },
    { value: 'LOW', label: 'Low' },
  ];

  const slaOptions = [
    { value: 'all', label: 'All' },
    { value: 'healthy', label: 'On Track' },
    { value: 'breached', label: 'Overdue' },
  ];

  const toggleStatus = (val: string) => {
    setStatusFilter((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
    setPage(1);
  };

  const togglePriority = (val: string) => {
    setPriorityFilter((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
    setPage(1);
  };

  const hasActiveFilters = statusFilter.length > 0 || priorityFilter.length > 0 || slaFilter !== 'all';

  const getFilterLabel = (type: 'status' | 'priority' | 'sla') => {
    if (type === 'status') return statusFilter.length > 0 ? `Status: ${statusFilter.length}` : 'Status';
    if (type === 'priority') return priorityFilter.length > 0 ? `Priority: ${priorityFilter.length}` : 'Priority';
    return slaFilter !== 'all' ? `SLA: ${slaFilter === 'breached' ? 'Overdue' : 'On Track'}` : 'SLA';
  };

  const isFilterActive = (type: 'status' | 'priority' | 'sla') => {
    if (type === 'status') return statusFilter.length > 0;
    if (type === 'priority') return priorityFilter.length > 0;
    return slaFilter !== 'all';
  };

  // ── Sortable Header ──────────────────────────────────────

  const SortableHeader = ({ field, label, align }: { field: SortField; label: string; align?: 'left' | 'center' | 'right' }) => {
    const isActive = sortBy === field;
    return (
      <TableCell
        align={align}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
          '&:hover': { color: theme.palette.primary.main },
        }}
        onClick={() => handleSort(field)}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          {isActive ? (
            sortOrder === 'asc' ? <ArrowUpIcon sx={{ fontSize: 14 }} /> : <ArrowDownIcon sx={{ fontSize: 14 }} />
          ) : (
            <SortIcon sx={{ fontSize: 14, opacity: 0.4 }} />
          )}
        </Box>
      </TableCell>
    );
  };

  // ── Render ──────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
      {/* Header Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Engineering Review Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve alternative parts for Form-Fit-Function equivalency
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/engineering/inbox-mock')}
            sx={{ textTransform: 'none', color: theme.palette.info.main }}
          >
            View Worklist mock →
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AssignmentIcon />}
            onClick={() => navigate('/engineering/reviews')}
            sx={{ textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
          >
            All Reviews
          </Button>
        </Stack>
      </Box>

      {/* KPI Strip */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 1.5,
          mb: 3,
        }}
      >
        {stats.map((stat) => (
          <Paper
            key={stat.label}
            elevation={0}
            onClick={stat.filter}
            sx={{
              px: 2,
              py: 1.5,
              cursor: stat.filter ? 'pointer' : 'default',
              border: '1px solid',
              borderColor: stat.active ? theme.palette.primary.main : theme.palette.divider,
              bgcolor: stat.active ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
              borderRadius: 1.5,
              transition: 'all 0.15s',
              '&:hover': stat.filter ? {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              } : {},
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {stat.value}{stat.suffix || ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {stat.label}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Compact Filter Chips */}
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 2 }}>
        <FilterIcon sx={{ fontSize: 16, color: theme.palette.text.disabled, mr: 0.25 }} />
        {(['status', 'priority', 'sla'] as const).map((type) => (
          <Chip
            key={type}
            label={getFilterLabel(type)}
            size="small"
            variant={isFilterActive(type) ? 'filled' : 'outlined'}
            color={isFilterActive(type) ? 'primary' : 'default'}
            onClick={(e) => setFilterAnchor({ el: e.currentTarget, type })}
            onDelete={isFilterActive(type) ? () => {
              if (type === 'status') { setStatusFilter([]); }
              else if (type === 'priority') { setPriorityFilter([]); }
              else { setSlaFilter('all'); }
              setPage(1);
            } : undefined}
            sx={{
              height: 28,
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
              '& .MuiChip-label': { px: 1 },
              '& .MuiChip-deleteIcon': { fontSize: 16 },
            }}
          />
        ))}
        {hasActiveFilters && (
          <Chip
            label="Clear all"
            size="small"
            variant="outlined"
            onClick={() => { setStatusFilter(['PENDING', 'IN_PROGRESS']); setPriorityFilter([]); setSlaFilter('all'); setPage(1); }}
            sx={{ height: 28, fontSize: '0.7rem', color: theme.palette.text.secondary, borderStyle: 'dashed', '& .MuiChip-label': { px: 1 } }}
          />
        )}
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
          {queueData?.total ?? 0} results
        </Typography>
      </Stack>

      {/* Filter Popover */}
      <Popover
        open={!!filterAnchor}
        anchorEl={filterAnchor?.el}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 1.5, minWidth: 180, boxShadow: theme.shadows[4] } } }}
      >
        <List dense disablePadding sx={{ py: 0.5 }}>
          {filterAnchor?.type === 'status' && statusOptions.map((opt) => (
            <ListItemButton key={opt.value} dense onClick={() => toggleStatus(opt.value)} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox size="small" checked={statusFilter.includes(opt.value)} sx={{ p: 0.25 }} />
              </ListItemIcon>
              <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
            </ListItemButton>
          ))}
          {filterAnchor?.type === 'priority' && priorityOptions.map((opt) => (
            <ListItemButton key={opt.value} dense onClick={() => togglePriority(opt.value)} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox size="small" checked={priorityFilter.includes(opt.value)} sx={{ p: 0.25 }} />
              </ListItemIcon>
              <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
            </ListItemButton>
          ))}
          {filterAnchor?.type === 'sla' && slaOptions.map((opt) => (
            <ListItemButton
              key={opt.value}
              dense
              selected={slaFilter === opt.value}
              onClick={() => { setSlaFilter(opt.value); setPage(1); setFilterAnchor(null); }}
              sx={{ py: 0.5 }}
            >
              <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
            </ListItemButton>
          ))}
        </List>
      </Popover>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
          Failed to load engineering reviews. Please try again.
        </Alert>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
        {isLoading && <LinearProgress />}
        <TableContainer>
          <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: '0 8px', px: 1 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: theme.palette.text.secondary, py: 1.5, border: 0, whiteSpace: 'nowrap', bgcolor: 'transparent' } }}>
                <TableCell sx={{ pl: 2.5 }}>Master Part</TableCell>
                <TableCell>Alternative Part</TableCell>
                <TableCell>Supplier</TableCell>
                <SortableHeader field="confidence" label="FFF Score" />
                <TableCell>Match</TableCell>
                <TableCell>Status</TableCell>
                <SortableHeader field="sla" label="SLA" />
                <TableCell align="center" sx={{ width: 48 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <TableRow key={idx}>
                    {Array.from({ length: 8 }).map((_, ci) => (
                      <TableCell key={ci}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : queueData?.data?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No reviews found matching your filters</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                queueData?.data?.map((review) => {
                  const slaInfo = getSLAInfo(review);
                  const fffScore = review.fffScore ?? review.initialConfidenceScore ?? review.confidenceScore ?? 0;
                  const confPct = Math.round(fffScore * (fffScore <= 1 ? 100 : 1));

                  return (
                    <TableRow
                      key={review._id}
                      hover
                      onClick={() => handleRowClick(review)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'background.paper',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        transition: 'box-shadow 120ms ease, background-color 120ms ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                        },
                        '& td': { py: 1.5, border: 0 },
                        '& td:first-of-type': {
                          borderTopLeftRadius: 8,
                          borderBottomLeftRadius: 8,
                          borderLeft: '4px solid',
                          borderLeftColor: getPriorityBorderColor(review.priority),
                          pl: 2,
                        },
                        '& td:last-of-type': {
                          borderTopRightRadius: 8,
                          borderBottomRightRadius: 8,
                        },
                      }}
                    >
                      {/* Master Part (left accent stripe encodes priority) */}
                      <TableCell>
                        <Tooltip title={`Priority: ${review.priority}`} placement="right">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                              {review.masterPartNumber || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 200 }}>
                              {review.masterPartDescription || ''}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>

                      {/* Alternative Part */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                          {review.alternativePartNumber || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 200 }}>
                          {review.alternativePartDescription || ''}
                        </Typography>
                      </TableCell>

                      {/* Supplier */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {review.alternativeManufacturer || '-'}
                        </Typography>
                      </TableCell>

                      {/* Confidence */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: alpha(theme.palette.divider, 0.5), overflow: 'hidden' }}>
                            <Box sx={{ width: `${confPct}%`, height: '100%', bgcolor: getConfidenceColor(confPct), borderRadius: 2 }} />
                          </Box>
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500, minWidth: 32 }}>
                            {confPct}%
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Match Type */}
                      <TableCell>
                        <Chip
                          label={review.matchType?.includes('FFF') || review.matchType?.includes('EQUIVALENT') ? 'FFF Equivalent' : review.matchType?.replace(/_/g, ' ') || 'N/A'}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            color: theme.palette.info.dark,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={review.status.replace(/_/g, ' ')}
                          size="small"
                          color={getStatusColor(review.status) as any}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, '& .MuiChip-label': { px: 1 } }}
                        />
                      </TableCell>

                      {/* SLA */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {slaInfo.severity === 'error' && <TimeIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />}
                          {slaInfo.severity === 'warning' && <TimeIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />}
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontSize: '0.8125rem',
                              fontWeight: slaInfo.severity !== 'default' ? 600 : 400,
                              color: slaInfo.severity === 'error' ? theme.palette.error.main
                                : slaInfo.severity === 'warning' ? theme.palette.warning.dark
                                : theme.palette.text.secondary,
                            }}
                          >
                            {slaInfo.label}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Open */}
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Open review">
                          <IconButton size="small" onClick={() => handleRowClick(review)}>
                            <OpenIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {queueData && queueData.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={queueData.totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default EngineeringReviewQueuePage;
